/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903263793 — useAppStore + useTimerStore */

import { useEffect, useRef } from 'react';

import { TIMER_START_VALUE } from '../constants';
import { debug } from '../lib/general';
import {
    clearSavedTimerEnd,
    loadSavedTimerEnd,
    storeTimerEnd,
} from '../lib/stateStorage';
import { useTimerStore } from './useTimerStore';

// Persisting the absolute end-time (Date.now() + duration) in localStorage keeps
// the frontend countdown in sync with the apparatus's own internal session
// timer on reload. On mount, start() reuses any future-dated saved end-time
// instead of restarting from `duration`. Cleared on tick-expiration and on
// reload-with-past-endTime path. Helpers live in shared/lib/stateStorage.ts
// alongside the other localStorage helpers (load/store/clear pattern).

const useTimer = (duration: number = TIMER_START_VALUE) => {
    const timer = useTimerStore((s) => s.time);
    const setTimer = useTimerStore((s) => s.setTime);
    const setShowTimer = useTimerStore((s) => s.setVisible);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const endTimeRef = useRef<number | null>(null);
    useEffect(() => {
        if (duration) setTimer(duration);
    }, [duration]);
    const clear = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    const tick = () => {
        if (!endTimeRef.current) return;

        const secondsLeft = Math.max(
            Math.ceil((endTimeRef.current - Date.now()) / 1000),
            0,
        );
        setTimer(secondsLeft);

        if (secondsLeft < 0) {
            debug('Timer expired', 'info');
            clear();
            clearSavedTimerEnd();
            setShowTimer(false);
            setTimer(TIMER_START_VALUE);
        }
    };

    const start = () => {
        const saved = loadSavedTimerEnd();
        const currentScope = window.location.pathname;
        const isSameScope = saved !== null && saved.scope === currentScope;

        if (isSameScope && saved.endTime > Date.now()) {
            debug(
                `Timer resume from saved end-time (${Math.ceil((saved.endTime - Date.now()) / 1000)}s left)`,
                'info',
            );
            endTimeRef.current = saved.endTime;
        } else if (isSameScope) {
            // Saved end-time is already in the past for this same screen —
            // the apparatus session expired while the page was reloading.
            // Surface immediate timeout instead of a fresh restart, so the
            // screen's `time <= 0` effect navigates to error/canceled
            // (matches apparatus reality).
            debug('Timer already expired on reload, triggering timeout', 'warn');
            clearSavedTimerEnd();
            setShowTimer(false);
            setTimer(0);
            return;
        } else {
            // No saved value, or value belongs to a different URL (different
            // order / vm / screen / cross-tab different-context) — start fresh.
            if (saved) clearSavedTimerEnd();
            endTimeRef.current = Date.now() + duration * 1000;
            storeTimerEnd(endTimeRef.current, currentScope);
        }
        setShowTimer(true);
        tick();
        intervalRef.current = setInterval(tick, 1000);
    };

    const reset = () => {
        debug('Timer reset', 'info');
        clear();
        // Do NOT clear localStorage TIMER_END_TIMESTAMP here. reset() runs
        // from screen useEffect cleanup on every unmount — including the
        // cross-tab-takeover navigate to /session-expired. Clearing storage
        // in cleanup would race-condition the new tab's useTimer.start(),
        // which expects to read the saved end-time and resume in sync with
        // the apparatus countdown. Stale values are auto-ignored on a
        // different URL pathname via the scope check in start().
        setShowTimer(false);
        setTimer(TIMER_START_VALUE);
        endTimeRef.current = null;
    };

    useEffect(() => {
        return () => {
            debug('Timer cleanup', 'info');
            clear();
        };
    }, []);

    return {
        time: timer,
        start,
        reset,
    };
};

export { useTimer };
